import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { STAGE_COLORS, PRIORITY_COLORS, stageOf, fmtDate, waLink } from "@/lib/crm";
import {
  CalendarClock, AlarmClock, Flame, Users, Phone, MessageCircle, Eye,
  Loader2, ListChecks, Star,
} from "lucide-react";

const TABS = [
  { k: "todaysFollowUps", label: "Today's Follow-ups", icon: CalendarClock, accent: "#0B2A6B" },
  { k: "overdue", label: "Overdue", icon: AlarmClock, accent: "#EF4444" },
  { k: "highPriority", label: "High Priority", icon: Flame, accent: "#D4A24C" },
];

export default function AdminWorkQueue() {
  const [data, setData] = useState(null);
  const [allCounsellors, setAllCounsellors] = useState([]);
  const [counsellor, setCounsellor] = useState("");
  const [tab, setTab] = useState("todaysFollowUps");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = counsellor ? { counsellor } : {};
    api.get("/admin/analytics/workqueue", { params })
      .then((r) => {
        setData(r.data);
        if (!counsellor) setAllCounsellors(r.data.counsellors || []);
      })
      .finally(() => setLoading(false));
  }, [counsellor]);

  useEffect(() => { load(); }, [load]);

  // Auto-open the most urgent tab that has pending work, so the screen never looks empty.
  const didDefault = useRef(false);
  useEffect(() => {
    if (!data || didDefault.current) return;
    didDefault.current = true;
    const order = ["overdue", "todaysFollowUps", "highPriority"];
    const first = order.find((k) => (data[k]?.length || 0) > 0);
    if (first) setTab(first);
  }, [data]);

  const rows = data?.[tab] || [];

  return (
    <div data-testid="admin-workqueue-page" className="space-y-6">
      <SEOHead title="Work Queue" />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ListChecks className="h-6 w-6 text-[#0B2A6B]" /> Counsellor Work Queue
          </h1>
          <p className="text-sm text-slate-500">Your daily admission pipeline — act on the right leads first</p>
        </div>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-slate-400" />
          <select
            data-testid="wq-counsellor-filter"
            value={counsellor}
            onChange={(e) => setCounsellor(e.target.value)}
            className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#0B2A6B]"
          >
            <option value="">All Counsellors</option>
            {allCounsellors.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard testid="wq-sum-today" icon={CalendarClock} color="#0B2A6B" label="Today's Follow-ups" value={data?.todaysFollowUps?.length ?? 0} />
        <SummaryCard testid="wq-sum-overdue" icon={AlarmClock} color="#EF4444" label="Overdue" value={data?.overdue?.length ?? 0} />
        <SummaryCard testid="wq-sum-hot" icon={Flame} color="#D4A24C" label="High Priority" value={data?.highPriority?.length ?? 0} />
        <SummaryCard testid="wq-sum-assigned" icon={Users} color="#10B981" label="Total Assigned" value={data?.assignedCount ?? 0} />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        {TABS.map((t) => {
          const count = data?.[t.k]?.length ?? 0;
          const active = tab === t.k;
          return (
            <button
              key={t.k}
              data-testid={`wq-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                active ? "border-[#0B2A6B] text-[#0B2A6B]" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <t.icon className="h-4 w-4" style={{ color: active ? t.accent : undefined }} /> {t.label}
              <span className={`rounded-full px-2 py-0.5 text-xs ${active ? "bg-[#0B2A6B] text-white" : "bg-slate-100 text-slate-600"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-9 w-9 animate-spin text-[#0B2A6B]" /></div>
      ) : rows.length === 0 ? (
        <div data-testid="wq-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
          {((data?.overdue?.length || 0) + (data?.todaysFollowUps?.length || 0) + (data?.highPriority?.length || 0)) === 0 ? (
            <p className="text-slate-500 font-medium">You're all caught up — no pending follow-ups right now. 🎉</p>
          ) : (
            <p className="text-slate-400">No leads in this bucket. Check the other tabs for pending work.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="wq-list">
          {rows.map((l) => <QueueRow key={l.id} lead={l} highlightOverdue={tab === "overdue"} />)}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${color}1a` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </span>
      <p className="mt-3 font-display text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function QueueRow({ lead, highlightOverdue }) {
  const stage = stageOf(lead);
  return (
    <div
      data-testid={`wq-row-${lead.id}`}
      className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${
        highlightOverdue ? "border-red-200" : "border-slate-200"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0B2A6B] text-sm font-bold text-white">
          {(lead.name || "?")[0]?.toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 truncate">{lead.name}</p>
          <p className="text-xs text-slate-500 truncate">{lead.mobile} · {lead.courseInterest || "No course"}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STAGE_COLORS[stage]}`}>{stage}</span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${PRIORITY_COLORS[lead.priority] || PRIORITY_COLORS.Medium}`}>{lead.priority || "Medium"}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#FDF9F1] px-2.5 py-0.5 text-xs font-semibold text-[#C08E3B]"><Star className="h-3 w-3" /> {lead.leadScore ?? 0}</span>
        <span className={`text-xs font-medium ${highlightOverdue ? "text-red-600" : "text-slate-500"}`}>
          Follow-up: {fmtDate(lead.nextFollowUp)}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <a data-testid={`wq-wa-${lead.id}`} href={waLink(lead)} target="_blank" rel="noreferrer" className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#25D366] text-white hover:opacity-90" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>
        <a data-testid={`wq-call-${lead.id}`} href={`tel:${lead.mobile}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#0B2A6B] text-white hover:bg-[#081E4D]" title="Call"><Phone className="h-4 w-4" /></a>
        <Link data-testid={`wq-view-${lead.id}`} to={`/admin/leads/${lead.id}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#0B2A6B] hover:bg-slate-50"><Eye className="h-4 w-4" /> Open</Link>
      </div>
    </div>
  );
}
